import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";

import { ACCEPT_INVITE } from "./queries";
import foo_logo from "../../assets/foo_mascot_chef_hat@2x.svg";
import { Settings } from "../../../models/settings_model";

const AcceptInvitePage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("t") || "";

  const [error, setError] = useState<string | false>(false);
  const [form, setForm] = useState({
    name: "",
    lastname: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [acceptInvite, { loading }] = useMutation(ACCEPT_INVITE);

  const handleType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setForm({ ...form, [id]: value });
  };

  const handleSubmit = async () => {
    setError(false);

    if (!token) {
      setError("Enlace de invitación no válido.");
      return;
    }
    if (form.name.length < 2 || form.lastname.length < 2) {
      setError("Favor de agregar tu nombre y apellido.");
      return;
    }
    if (form.phone.length < 7) {
      setError("Favor de agregar un número de teléfono válido.");
      return;
    }
    if (form.password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    try {
      const { data } = await acceptInvite({
        variables: {
          token,
          name: form.name,
          lastname: form.lastname,
          phone: form.phone,
          password: form.password,
        },
      });

      if (data?.acceptInvite) {
        const authToken = data.acceptInvite.token;
        const user = { ...data.acceptInvite.user };
        const settings = { ...data.acceptInvite.settings };
        const settings_obj: Settings = {};
        Object.keys(settings).forEach((setting_key) => {
          const setting = settings[setting_key];
          const pref_key = setting.pref_key as keyof Settings;
          delete setting.pref_key;
          if (
            setting.pref_value === "true" ||
            setting.pref_value === "false"
          ) {
            setting.pref_value = setting.pref_value === "true";
          } else if (!isNaN(setting.pref_value)) {
            setting.pref_value = parseInt(setting.pref_value);
          }
          settings_obj[pref_key] = setting.pref_value;
        });
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("settings", JSON.stringify(settings_obj));
        localStorage.setItem("token", authToken);
        window.location.href = "/";
      }
    } catch (err: any) {
      const message =
        err?.graphQLErrors?.[0]?.message ||
        "Error al aceptar la invitación. Intenta de nuevo.";
      setError(message);
    }
  };

  const enterPressed = (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter") {
      handleSubmit();
    }
  };

  if (!token) {
    return (
      <main>
        <div className="container login_div">
          <div className="row justify-content-center">
            <div className="col-10 col-sm-5 col-lg-4 text-center">
              <img
                src={foo_logo}
                className="img-fluid mb-4"
                style={{ maxWidth: 120 }}
                alt="Foo Logo"
              />
              <h3>Enlace no válido</h3>
              <p className="text-muted">
                Este enlace de invitación no es válido. Pide a tu administrador
                que te reenvíe la invitación.
              </p>
              <Link to="/login" className="btn btn-primary">
                Ir a Iniciar Sesión
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container login_div">
        <div className="row justify-content-center">
          <div className="col-10 col-sm-6 col-lg-4">
            <div className="text-center mb-4">
              <img
                src={foo_logo}
                className="img-fluid"
                style={{ maxWidth: 120 }}
                alt="Foo Logo"
              />
              <h2>Completa tu cuenta</h2>
              <p className="text-muted">
                Fuiste invitado a unirte como empleado. Completa tus datos para
                activar tu cuenta.
              </p>
            </div>

            {error && (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle"></i> {error}
              </div>
            )}

            <div className="row">
              <div className="col-6">
                <div className="form-group">
                  <label htmlFor="name">Nombre</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleType}
                    onKeyPress={enterPressed}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label htmlFor="lastname">Apellido</label>
                  <input
                    type="text"
                    id="lastname"
                    className="form-control"
                    value={form.lastname}
                    onChange={handleType}
                    onKeyPress={enterPressed}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Teléfono</label>
              <input
                type="text"
                id="phone"
                className="form-control"
                value={form.phone}
                onChange={handleType}
                onKeyPress={enterPressed}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <input
                type="password"
                id="password"
                className="form-control"
                value={form.password}
                onChange={handleType}
                onKeyPress={enterPressed}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Contraseña</label>
              <input
                type="password"
                id="confirmPassword"
                className="form-control"
                value={form.confirmPassword}
                onChange={handleType}
                onKeyPress={enterPressed}
              />
            </div>

            <button
              type="button"
              className="btn btn-primary btn-block btn_xs_lg"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Activando cuenta..." : "Activar Cuenta"}
            </button>

            <p className="mt-3 text-center">
              <small className="text-dark">
                Ya tienes cuenta? <Link to="/login">Iniciar Sesión</Link>
              </small>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AcceptInvitePage;
